export const normalizeSearchString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
};

export interface SearchInStringOptions {
  target: string;
  searchString: string;
  normalize?: boolean;
}

export const searchInString = ({
  target,
  searchString,
  normalize = true,
}: SearchInStringOptions): boolean => {
  if (!searchString || searchString.length === 0) {
    return true;
  }

  if (!target) {
    return false;
  }

  const processedTarget = normalize
    ? normalizeSearchString(target)
    : target.toLowerCase();
  const processedSearch = normalize
    ? normalizeSearchString(searchString)
    : searchString.toLowerCase();

  return processedTarget.includes(processedSearch);
};

export interface SearchMatchSegment {
  text: string;
  isMatch: boolean;
}

export interface HighlightSearchMatchesOptions {
  target: string;
  searchString: string;
  normalize?: boolean;
}

export const highlightSearchMatches = ({
  target,
  searchString,
  normalize = true,
}: HighlightSearchMatchesOptions): SearchMatchSegment[] => {
  if (!searchString || searchString.length === 0 || !target) {
    return [{ text: target || '', isMatch: false }];
  }

  const normalizedSearch = normalize
    ? normalizeSearchString(searchString)
    : searchString.toLowerCase();

  if (normalizedSearch.length === 0) {
    return [{ text: target, isMatch: false }];
  }

  const normalizedTarget = normalize
    ? target
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    : target.toLowerCase();

  const escapedSearch = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexPattern = normalize
    ? escapedSearch.replace(/\s+/g, '\\s+')
    : escapedSearch;
  const regex = new RegExp(regexPattern, 'gi');

  const segments: SearchMatchSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = regex.exec(normalizedTarget);

  while (match !== null) {
    const { index } = match;
    const matchLength = match[0].length;

    if (index > lastIndex) {
      segments.push({
        text: target.substring(lastIndex, index),
        isMatch: false,
      });
    }

    segments.push({
      text: target.substring(index, index + matchLength),
      isMatch: true,
    });

    lastIndex = index + matchLength;

    if (matchLength === 0) {
      regex.lastIndex++;
    }

    match = regex.exec(normalizedTarget);
  }

  if (lastIndex < target.length) {
    segments.push({
      text: target.substring(lastIndex),
      isMatch: false,
    });
  }

  return segments.length > 0 ? segments : [{ text: target, isMatch: false }];
};

export const StringHelper = {
  searchInString,
  highlightSearchMatches,
  normalizeSearchString,
};
